import { format, parseISO } from "date-fns";
import * as Yup from "yup";

const parseAmPmTime = (time: string) => {
    if (!time) return null;
    const match = time.match(/^(\d{1,2}):(\d{2})$/);
    if (!match) return null;
    const hour = parseInt(match[1], 10);
    const minute = parseInt(match[2], 10);
    if (hour < 0 || hour > 23 || minute < 0 || minute > 59) return null;
    return hour * 60 + minute;
};


export const getCandidateLogTimesheetSchema = (
  actualJobStartTime: string,
  actualJobEndTime: string,
  did_not_work?: boolean
) => {
  return {
    job_start_time: Yup.string()
      .when([], {
        is: () => did_not_work,
        then: (schema) => schema.notRequired(),
        otherwise: (schema) => schema.required("Job start time is required"),
      }),
    job_end_time: Yup.string()
      .when([], {
        is: () => did_not_work,
        then: (schema) => schema.notRequired(),
        otherwise: (schema) => schema.required("Job end time is required"),
      })
      .test(
      "is-after-start",
      "Job end time must be after job start time",
      function (value) {
        if (did_not_work) return true;
        const { job_start_time } = this.parent;
        if (!job_start_time || !value) return false;
        const start = parseAmPmTime(job_start_time);
        const end = parseAmPmTime(value);
        if (start === null || end === null) return false;
        return end > start;
      }
    ),
    break_start_time: Yup.string().test(
      "within-actual-job-time",
      "Break start time must be within selected job timeframe",
      function (value) {
        if (did_not_work) return true;
        if (!value) return true; // optional
        const v = parseAmPmTime(value);
        // Access sibling field values from the form
        const jobStart = parseAmPmTime(this.parent.job_start_time);
        const jobEnd = parseAmPmTime(this.parent.job_end_time);
        // Skip validation if job start or end is missing
        if (!this.parent.job_start_time || !this.parent.job_end_time) {
          return true;
        }

        // if (jobStart === null || jobEnd === null) {
        //   return this.createError({ message: "Select job start and end time" });
        // }
        if (v === null || jobStart === null || jobEnd === null) return false;
        return v >= jobStart && v <= jobEnd;
      }
    ),
    break_end_time: Yup.string()
  .test(
    "within-actual-job-time",
    "Break end time must be within selected job timeframe",
    function (value) {
      if (did_not_work) return true;
      if (!value) return true; // optional
      const jobStart = parseAmPmTime(this.parent.job_start_time);
      const jobEnd = parseAmPmTime(this.parent.job_end_time);
      const v = parseAmPmTime(value);

      // 👇 Skip validation if job start or end is missing
      if (!this.parent.job_start_time || !this.parent.job_end_time) {
        return true;
      }

      if (jobStart === null || jobEnd === null || v === null) return false;
      return v >= jobStart && v <= jobEnd;
    }
  )
  .test(
    "is-after-break-start",
    "Break end time must be after break start time",
    function (value) {
      if (did_not_work) return true;
      const { break_start_time } = this.parent;
      if (!value || !break_start_time) return true;
      const start = parseAmPmTime(break_start_time);
      const end = parseAmPmTime(value);
      if (start === null || end === null) return false;
      return end > start;
    }
  ),
  };
};

/**
 * Function to format job start and end times into a readable string.
 * It converts the start and end times to a specific format including day, date, month, year, and time in 12-hour format.
 * @param job_start_time
 * @param job_end_time
 * @returns
 */
export const formatJobTime = (
  job_start_time: string | null,
  job_end_time: string | null
) => {
  if (!job_start_time || !job_end_time) return "";
  const start = new Date(job_start_time);
  const end = new Date(job_end_time);

  // Use built-in toLocaleString for day and month abbreviations
  const day = start.toLocaleString("en-US", {
    weekday: "short",
    timeZone: "UTC",
  });
  const date = start.getUTCDate();
  const month = start.toLocaleString("en-US", {
    month: "short",
    timeZone: "UTC",
  });
  const year = start.getUTCFullYear();

  // Format time in 12-hour format with AM/PM
  const formatTime = (dateObj: Date) => {
    let hours = dateObj.getUTCHours();
    const minutes = dateObj.getUTCMinutes();
    const ampm = hours >= 12 ? "pm" : "am";
    hours = hours % 12;
    hours = hours ? hours : 12;
    return `${hours}:${minutes.toString().padStart(2, "0")}${ampm}`;
  };

  const startTime = formatTime(start);
  const endTime = formatTime(end);

  return `${day} ${date} ${month} ${year}, ${startTime} to ${endTime}`;
};

/**
 * Extracts the time from a date string and formats it into either 24-hour or 12-hour format.
 * If the date string is null or undefined, it returns an empty string.
 * @param dateString
 * @param isValue24HourFormat
 * @returns
 */
export const getTimeFromDate = (
  dateString?: string | null,
  isValue24HourFormat: boolean = false
) => {
  if (!dateString) return ""; // Handle null or undefined dateString
  const date = new Date(dateString);
  let hours = date.getUTCHours();
  const minutes = String(date.getUTCMinutes()).padStart(2, "0");
  if (isValue24HourFormat) {
    // Return in 24-hour format (HH:mm)
    return `${hours.toString().padStart(2, "0")}:${minutes}`;
  } else {
    const ampm = hours >= 12 ? "pm" : "am";
    let displayHours = hours % 12;
    displayHours = displayHours ? displayHours : 12; // the hour '0' should be '12'
    return `${displayHours}:${minutes}${ampm}`;
  }
};

export const formatReadableTimeSlot = (date: string, start: string, end: string) => {
  const dateObj = parseISO(date); // "2025-05-16"
  const startTime = parseISO(`${date}T${start}`); // "08:00:00"
  const endTime = parseISO(`${date}T${end}`); // "20:00:00"

  const formattedDate = format(dateObj, "EEE d MMM yyyy"); // e.g. "Fri 16 May 2025"
  const formattedStart = format(startTime, "h:mmaaa"); // e.g. "8:00AM"
  const formattedEnd = format(endTime, "h:mmaaa"); // e.g. "8:00PM"

  return `${formattedDate}, ${formattedStart.toLowerCase()} to ${formattedEnd.toLowerCase()}`;
};